import React from 'react';
import { Button, Card, Spin, Typography } from 'antd';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

type VerifyStatus = 'idle' | 'needsLogin' | 'verifying' | 'success' | 'error';

const WhatsAppVerify: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<VerifyStatus>('idle');
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  React.useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Missing verification token.');
      return;
    }

    if (!currentUser) {
      localStorage.setItem('pendingWhatsAppVerifyToken', token);
      setStatus('needsLogin');
      return;
    }

    const verify = async () => {
      try {
        setStatus('verifying');
        await axios.get(ENDPOINTS.WHATSAPP_VERIFY(token), { withCredentials: true });
        localStorage.removeItem('pendingWhatsAppVerifyToken');
        setStatus('success');
      } catch (error: any) {
        console.error('WhatsApp verification failed', error);
        if (error?.response?.status === 401) {
          localStorage.setItem('pendingWhatsAppVerifyToken', token);
          setStatus('needsLogin');
          return;
        }
        setStatus('error');
        setErrorMessage(error?.response?.data?.message || 'Verification failed. Please try again.');
      }
    };

    verify();
  }, [token, currentUser]);

  const renderContent = () => {
    if (status === 'needsLogin') {
      return (
        <>
          <Title level={3}>Login required</Title>
          <Text type="secondary">
            Please log in to verify your WhatsApp number.
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate('/login')}>
              Go to login
            </Button>
          </div>
        </>
      );
    }

    if (status === 'verifying') {
      return (
        <>
          <Title level={3}>Verifying your WhatsApp number…</Title>
          <Spin style={{ marginTop: 16 }} />
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Title level={3}>WhatsApp number verified</Title>
          <Text type="secondary">
            You can now use WhatsApp with OSCAR.
          </Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => navigate('/configuration')}>
              Go to settings
            </Button>
          </div>
        </>
      );
    }

    if (status === 'error') {
      return (
        <>
          <Title level={3}>Verification failed</Title>
          <Text type="secondary">{errorMessage}</Text>
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => navigate('/configuration')}>
              Back to settings
            </Button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 16px' }}>
      <Card style={{ maxWidth: 520, width: '100%' }}>
        {renderContent()}
      </Card>
    </div>
  );
};

export default WhatsAppVerify;
